import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "antd";
import { FormattedMessage } from "react-intl";
import { useDispatch } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import {
  ConnectComponentsProvider,
  ConnectNotificationBanner,
} from "@stripe/react-connect-js";
import Header from "../../components/Header/Header";
import { BackButton } from "../../components/General/BackButton";
import checkMark from "../../assets/images/checkmark.png";
import { useConnectOnboarding } from "../../hooks/useConnectOnboarding";
import { readAuthToken } from "../../utils/axiosMiddleware";
import {
  isWebViewSession,
  markWebViewSession,
} from "../../utils/webViewSession";
import { ROUTES } from "../../constants/routes";
import { stripe_publishable_key } from "../../constants";
import { createNotificationSession } from "../../api/stripeApi/notificationSession";
import { WebViewReturnPanel } from "./WebViewReturnPanel";
import { BrowserReturnPanel } from "./BrowserReturnPanel";
import PayoutEmailModal from "../../components/Stripe/PayoutEmailModal";
import {
  canMountBanner,
  getBannerCopy,
  resolveBannerState,
  shouldRequestSession,
} from "./notificationBannerState";

/**
 * Stripe Connect payout onboarding screen. Redirects to Stripe's hosted
 * onboarding (identity + bank collected on Stripe's domain — no raw SSN/bank in
 * our app, replacing the Everee/AeroSync/GigWage forms) and polls the payout
 * status on return until `payouts_enabled`.
 *
 * This route is also the Connect `return_url` target (`/stripe/return`). Inside
 * the mobile WebView that return has no `?token=`, so the status poll cannot
 * run and the desktop layout rendered an empty page — there, show the
 * return-to-app panel instead of the web chrome.
 *
 * It also hosts the Connect **notification banner**, which is why this URL is
 * registered as the "Notification banner" Site link. Two reasons it lives here
 * rather than on the tax page: Stripe will not create a live-mode Account
 * Session at all until that URL is registered, and the banner is the surface
 * Stripe directs remediation traffic at — account management alone does not
 * prompt for missing information. The banner is mounted only on the default
 * branch below; the two return branches serve public routes with no guaranteed
 * session, and a WebView cannot render embedded components at all.
 */
export const StripeOnboarding = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const {
    status,
    loading,
    startOnboarding,
    refresh,
    emailRequired,
    submitPayoutEmail,
    dismissEmailPrompt,
  } = useConnectOnboarding(dispatch);
  const enabled = status?.payouts_enabled;
  // Has the user actually begun onboarding? `status` itself is truthy for ANY
  // account, so keying the CTA on it showed "Continue" to people who never
  // started. `details_submitted` is what Stripe flips once details are sent.
  const hasStarted = Boolean(status?.details_submitted);
  const userId = readAuthToken()?.user_id;
  // Only the Stripe return target swaps in the WebView panel. The onboarding
  // screen itself keeps its normal UI in the WebView — it still has the token,
  // so its status poll works and the user needs the "set up payouts" button.
  const isReturn = pathname === ROUTES.STRIPE_RETURN;
  const showReturnPanel = isReturn && isWebViewSession(token);
  // A browser coming back from Stripe used to fall through to the normal
  // screen, which needs a session — and this route sat behind the auth gate, so
  // an unmatched route rendered a blank page instead. It now renders publicly,
  // and says what happened rather than showing setup UI to someone who has just
  // finished setup.
  //
  // Known gap: a WebView with sessionStorage unavailable (private mode) loses
  // the marker and lands here instead of the return-to-app panel, since the
  // `?token=` is gone after the Stripe round-trip. That is a narrower failure
  // than the blank page it replaces, and closing it properly needs a detector
  // that does not depend on storage.
  const showBrowserReturn = isReturn && !showReturnPanel;

  // Notification banner session. Deliberately a SECOND data source:
  // `useConnectOnboarding` reports {payouts_enabled, charges_enabled,
  // details_submitted} and carries no client secret or account id, so it cannot
  // feed an embedded component. The poll stays the gate, this stays the payload.
  const [bannerSession, setBannerSession] = useState(null);
  const [bannerFailed, setBannerFailed] = useState(false);
  const [notificationCount, setNotificationCount] = useState(null);
  // StrictMode runs mount effects twice in development; without this one visit
  // opens two account sessions. Same guard as the tax page.
  const bannerRequestedRef = useRef(false);
  // The secret from the readiness probe, reused for Stripe's first fetch so the
  // visit costs one session instead of two.
  const bannerSecretRef = useRef(null);
  const firstFetchRef = useRef(true);

  const bannerState = resolveBannerState({
    signedIn: Boolean(userId),
    // `status` starts null and lands a round-trip later — absent is not "no".
    statusLoaded: status !== null,
    detailsSubmitted: hasStarted,
    clientSecret: bannerSession?.clientSecret,
    unavailable: Boolean(bannerSession?.unavailable),
    failed: bannerFailed,
    inWebView: isWebViewSession(token),
  });
  const bannerReady = canMountBanner(bannerState);
  const bannerCopy = getBannerCopy(bannerState);

  const requestBannerSession = useCallback(async () => {
    const data = await createNotificationSession();
    if (!data) {
      setBannerFailed(true);
      return;
    }
    // Held in a ref as well as state: state drives the READY gate, the ref is
    // what Stripe's first `fetchClientSecret` hands back. Without it that probe
    // secret is discarded and Stripe mints a second session for the same visit.
    bannerSecretRef.current = data.clientSecret;
    setBannerSession(data);
  }, []);

  useEffect(() => {
    if (!shouldRequestSession(bannerState) || bannerRequestedRef.current) {
      return;
    }
    bannerRequestedRef.current = true;
    requestBannerSession();
  }, [bannerState, requestBannerSession]);

  // One Connect instance for the page, keyed on the boolean rather than the
  // session object so the 4s status poll cannot churn it — a new instance per
  // tick would be 24 account sessions per visit against a 60-req/min budget.
  //
  // Reaching READY already cost one session, and Stripe calls
  // `fetchClientSecret` again as it initialises. Handing back the held secret on
  // that FIRST call makes the visit cost one session rather than two; every
  // later call mints fresh, because Stripe also uses this to refresh an expired
  // session and returning a cached secret there would make the refresh a silent
  // no-op — the bug the tax page documents.
  //
  // Refs, not state, so the memo's `[bannerReady]` key stays stable.
  const connectInstance = useMemo(() => {
    if (!bannerReady) {
      return null;
    }
    return loadConnectAndInitialize({
      publishableKey: stripe_publishable_key,
      fetchClientSecret: async () => {
        if (firstFetchRef.current) {
          firstFetchRef.current = false;
          return bannerSecretRef.current;
        }
        return (await createNotificationSession())?.clientSecret;
      },
    });
  }, [bannerReady]);

  // Persist the WebView marker on the way in, so it is still readable when
  // Stripe returns to this route without the token param.
  useEffect(() => {
    if (token) {
      markWebViewSession();
    }
  }, [token]);

  if (showReturnPanel) {
    return <WebViewReturnPanel userId={userId} />;
  }

  if (showBrowserReturn) {
    return (
      <>
        <Header />
        <div className="container mt-4">
          <BrowserReturnPanel
            payoutsEnabled={Boolean(enabled)}
            detailsSubmitted={hasStarted}
            // `status` starts null and lands a round-trip later. Without this
            // the first paint reads absent as unfinished, so a user who has
            // just completed onboarding is told details are still needed.
            statusLoaded={status !== null}
            // No session means the status can never load: checkStatus bails
            // without a user id, so waiting would hang on the loader forever.
            signedIn={Boolean(userId)}
            loading={loading}
            onFinishSetup={startOnboarding}
            onRefresh={refresh}
          />
          {/* Finishing setup from here can also come back asking for an email,
              so the prompt has to exist on this branch too. */}
          <PayoutEmailModal
            open={emailRequired}
            loading={loading}
            onSubmit={submitPayoutEmail}
            onCancel={dismissEmailPrompt}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container mt-4">
        <BackButton />
        {/* Outside the centered column on purpose: the banner is full-width, and
            inside `align-items-center text-center` the Stripe iframe is squeezed
            into a narrow centred strip. A direct `.container` child, the same
            placement the tax page gives its Alert.

            Stripe renders nothing here when there is no outstanding requirement,
            so there is no empty state to build around it — and the heading waits
            for a reported count rather than sitting over blank space, since only
            the iframe knows whether it has anything to show. */}
        {bannerReady && (
          <section className="mb-4" data-testid="payout-notification-banner">
            {notificationCount > 0 && (
              <h5 className="fw-bold">
                <FormattedMessage id={bannerCopy.title} />
              </h5>
            )}
            <ConnectComponentsProvider connectInstance={connectInstance}>
              <ConnectNotificationBanner
                // Stripe's defaults, written out. `eventually_due` would surface
                // requirements that block nothing yet, turning a remediation
                // banner into a nag.
                collectionOptions={{
                  fields: "currently_due",
                  futureRequirements: "omit",
                }}
                onNotificationsChange={({ total }) =>
                  setNotificationCount(total)
                }
                // A banner that failed to load is worse than no banner: a blank
                // strip reads as broken. This cannot loop back into a re-mint —
                // `bannerRequestedRef` is already set, so HIDDEN is terminal.
                onLoadError={() => setBannerFailed(true)}
              />
            </ConnectComponentsProvider>
          </section>
        )}
        <div className="d-flex flex-column align-items-center mt-5 text-center">
          <h4 className="fw-bold">
            <FormattedMessage id="PAYOUT_SETUP" />
          </h4>

          {/* Status stays visible in BOTH states, with Refresh, so an enabled user
              can still confirm where they stand — mirrors mobile's payout screen. */}
          <div className="d-flex align-items-center mt-3">
            {enabled && (
              <img src={checkMark} alt="" className="checkmark me-2" />
            )}
            <span className="fw-bold" data-testid="payout-status">
              <FormattedMessage
                id={enabled ? "PAYOUTS_ENABLED" : "PAYOUTS_INCOMPLETE"}
              />
            </span>
          </div>

          {!enabled && (
            <>
              <p className="mt-3 w-75">
                <FormattedMessage id="PAYOUT_SETUP_DESC" />
              </p>
              {/* "in progress" only once Stripe has actually received details —
                  keyed on details_submitted, not on the status object existing,
                  which is truthy for any account including one never started. */}
              {hasStarted && (
                <p className="text-muted">
                  <FormattedMessage id="PAYOUTS_PENDING" />
                </p>
              )}
              <Button
                type="primary"
                onClick={startOnboarding}
                loading={loading}
                className="fw-bold mt-3"
                data-testid="payout-setup-cta"
              >
                <FormattedMessage
                  id={hasStarted ? "CONTINUE_PAYOUT_SETUP" : "SET_UP_PAYOUTS"}
                />
              </Button>
            </>
          )}
          <Button type="link" onClick={refresh} className="mt-2">
            <FormattedMessage id="REFRESH_STATUS" />
          </Button>
        </div>
        <PayoutEmailModal
          open={emailRequired}
          loading={loading}
          onSubmit={submitPayoutEmail}
          onCancel={dismissEmailPrompt}
        />
      </div>
    </>
  );
};

export default StripeOnboarding;
